import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { restoreBackup } from '../api/backups';

/**
 * Modal for confirming and configuring backup restore
 */
function BackupRestoreModal({ backup, workspaces, onClose, onRestored }) {
    const [targetWorkspace, setTargetWorkspace] = useState('');
    const [overwrite, setOverwrite] = useState(false);
    const [isRestoring, setIsRestoring] = useState(false);
    const [error, setError] = useState(null);

    const handleRestore = async () => {
        setIsRestoring(true);
        setError(null);

        try {
            await restoreBackup(backup.backup_id, {
                overwrite
            });
            onRestored?.();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsRestoring(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
                {/* Backdrop */}
                <div
                    className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                    onClick={onClose}
                />

                {/* Modal */}
                <div className="relative inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle bg-white rounded-lg shadow-xl transform transition-all">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 bg-amber-100 rounded-lg flex items-center justify-center">
                                <svg className="h-5 w-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-medium text-gray-900">
                                Restore Backup
                            </h3>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-500"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Backup Info */}
                    <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Backup Details</h4>
                        <dl className="space-y-1 text-sm">
                            <div className="flex justify-between">
                                <dt className="text-gray-500">Backup ID:</dt>
                                <dd className="text-gray-900 font-mono text-xs">{backup.backup_id}</dd>
                            </div>
                            <div className="flex justify-between">
                                <dt className="text-gray-500">Bot ID:</dt>
                                <dd className="text-gray-900">{backup.bot_id}</dd>
                            </div>
                            <div className="flex justify-between">
                                <dt className="text-gray-500">Created:</dt>
                                <dd className="text-gray-900">{formatDate(backup.created_at)}</dd>
                            </div>
                            <div className="flex justify-between">
                                <dt className="text-gray-500">Filename:</dt>
                                <dd className="text-gray-900 truncate max-w-[200px]" title={backup.filename}>
                                    {backup.filename}
                                </dd>
                            </div>
                        </dl>
                    </div>

                    {/* Warning */}
                    <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
                        <p className="text-sm text-amber-800">
                            Restoring a backup may overwrite existing data. Please ensure you have saved any important changes.
                        </p>
                    </div>

                    {/* Target Selection */}
                    <div className="mb-4">
                        <label htmlFor="target-workspace" className="block text-sm font-medium text-gray-700 mb-1">
                            Restore To
                        </label>
                        <select
                            id="target-workspace"
                            value={targetWorkspace}
                            onChange={(e) => setTargetWorkspace(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            disabled={isRestoring}
                        >
                            <option value="">Original workspace ({backup.bot_id})</option>
                            {workspaces.map(ws => (
                                <option key={ws.bot_id} value={ws.bot_id}>
                                    {ws.bot_id} (Existing)
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Overwrite Option */}
                    <div className="mb-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={overwrite}
                                onChange={(e) => setOverwrite(e.target.checked)}
                                className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                disabled={isRestoring}
                            />
                            <span className="text-sm text-gray-700">
                                Overwrite existing workspace data
                            </span>
                        </label>
                        {overwrite && (
                            <p className="mt-1 text-xs text-red-600">
                                Warning: This will permanently delete existing data in the target workspace.
                            </p>
                        )}
                    </div>

                    {/* Error */}
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                            <p className="text-sm text-red-600">{error}</p>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                            disabled={isRestoring}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleRestore}
                            className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 disabled:opacity-50"
                            disabled={isRestoring}
                        >
                            {isRestoring ? 'Restoring...' : 'Restore Backup'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

BackupRestoreModal.propTypes = {
    backup: PropTypes.shape({
        backup_id: PropTypes.string.isRequired,
        bot_id: PropTypes.string,
        filename: PropTypes.string,
        created_at: PropTypes.string,
        size_bytes: PropTypes.number
    }).isRequired,
    workspaces: PropTypes.arrayOf(PropTypes.shape({
        bot_id: PropTypes.string.isRequired,
        path: PropTypes.string
    })),
    onClose: PropTypes.func.isRequired,
    onRestored: PropTypes.func
};

BackupRestoreModal.defaultProps = {
    workspaces: []
};

export default BackupRestoreModal;
